import { useState, useCallback } from 'react';
import { defaultAxiosInstance } from '../../../api/defaultInstance';

/**
 * 날짜 수정 기능을 위한 커스텀 훅
 * @param {string} apiEndpoint - 날짜 수정 API 엔드포인트
 * @param {Function} onSuccess - 수정 성공 시 호출할 콜백 함수
 * @returns {Object} 날짜 수정 관련 상태 및 함수들
 */
export function useDateEdit(apiEndpoint = '/advertisement/admin/campaign/date', onSuccess) {
  const [editingDate, setEditingDate] = useState(null);
  const [loading, setLoading] = useState(false);

  /**
   * 날짜 편집 시작
   * @param {string|number} adCampaignMetaId - 캠페인 메타 ID
   * @param {string} startDate - 시작일
   * @param {string} endDate - 종료일
   */
  const startEdit = useCallback((adCampaignMetaId, startDate, endDate) => {
    setEditingDate({ adCampaignMetaId, startDate, endDate });
  }, []);

  /**
   * 날짜 편집 취소
   */
  const cancelEdit = useCallback(() => {
    setEditingDate(null);
  }, []);

  /**
   * 날짜 변경 저장
   * @param {Object} row - 현재 행 데이터
   * @param {string} newStartDate - 새로운 시작일
   */
  const handleChange = useCallback(async (row, newStartDate) => {
    if (!editingDate) return;

    const originalStartDate = new Date(editingDate.startDate);
    const originalEndDate = new Date(editingDate.endDate);
    const daysDiff = Math.ceil((originalEndDate - originalStartDate) / (1000 * 60 * 60 * 24));

    const newStartDateTime = new Date(newStartDate);
    const newEndDate = new Date(newStartDateTime);
    newEndDate.setDate(newEndDate.getDate() + daysDiff);

    try {
      setLoading(true);
      const response = await defaultAxiosInstance.put(apiEndpoint, {
        adCampaignMetaId: editingDate.adCampaignMetaId,
        startDate: newStartDate,
        endDate: newEndDate.toISOString().split('T')[0]
      });

      if (response.data.status === 200) {
        if (onSuccess) {
          await onSuccess();
        }
      }
    } catch (error) {
      console.error('날짜 수정 실패:', error);
      alert('날짜 수정에 실패했습니다.');
    } finally {
      setLoading(false);
      setEditingDate(null);
    }
  }, [editingDate, apiEndpoint, onSuccess]);

  /**
   * 현재 편집 중인 행인지 확인
   * @param {string|number} adCampaignMetaId - 확인할 캠페인 메타 ID
   * @returns {boolean}
   */
  const isEditing = useCallback((adCampaignMetaId) => {
    return editingDate && editingDate.adCampaignMetaId === adCampaignMetaId;
  }, [editingDate]);

  /**
   * 날짜 컬럼 렌더러 생성
   * 테이블 컬럼에서 사용할 수 있는 render 함수를 반환
   */
  const createDateColumnRenderer = useCallback(() => {
    return (row) => {
      if (isEditing(row.adCampaignMetaId)) {
        return (
          <>
            <div>
              <input
                type="date"
                defaultValue={row.startDate}
                onChange={(e) => handleChange(row, e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div><span className="date-red">{row.endDate}</span></div>
            <div>{row.executionDays}일</div>
          </>
        );
      } else {
        return (
          <>
            <div>
              <span
                className="date-blue"
                data-campaign-meta-id={row.adCampaignMetaId}
                data-start-date={row.startDate}
                data-end-date={row.endDate}
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  startEdit(row.adCampaignMetaId, row.startDate, row.endDate);
                }}
              >
                {row.startDate}
              </span>
            </div>
            <div><span className="date-red">{row.endDate}</span></div>
            <div>{row.executionDays}일</div>
          </>
        );
      }
    };
  }, [isEditing, handleChange, startEdit]);

  return {
    // 상태
    editingDate,
    loading,
    
    // 액션
    startEdit,
    cancelEdit,
    handleChange,
    
    // 유틸
    isEditing,
    createDateColumnRenderer
  };
}
